<script setup>
import {
  ref,
  reactive,
  onMounted,
  watch
} from "vue";
import Label from "@/components/Label";
import VueSelect from "vue-select";
import { api } from "@/services/api";
import { utils } from "@/utilities.js";

import { useFarmerStore } from "@/stores/farmer";
import { useHybridStore } from "@/stores/hybrid";
import { useContextStore } from "@/stores/context";

const contextStore = useContextStore();

const farmerStore = useFarmerStore();
const hybridStore = useHybridStore();

const farmerSelected = ref({});
const farmerReady = ref(false);
let farmersOptions = reactive([]);

const hybridSelected = ref([]);
const hybridReady = ref(false);
let hybridsOptions = reactive([]);

const props = defineProps(["myLocation", "activity"]);
const emits = defineEmits(["submitMaster"]);

const form = reactive({
  activity_type: "Harvest",
  date: utils.getCurrentDate(),
  attendance: 0,
  farmer_id: props.activity.schedulable_id ?? "",
  description: "",
  feedback: "",
  files: "",
  hybrids: [],
  planting_area: 0,
  budget_spent: props.activity?.budget ? parseFloat(props.activity.budget) : 0,
  hybrid_reserved: 0,
  yields_Kg: 0,

  check_out: true,
  sale_schedule: props.activity,

  latitude: props.myLocation.latitude,
  longitude: props.myLocation.longitude,
  current_latitude: props.myLocation.current_latitude,
  current_longitude: props.myLocation.current_longitude,
  distance: utils.getDistanceFromActivity(props.activity),

  isLaterActive: true,
});

onMounted(async () => {
  await farmerStore.initFarmers();
  farmersOptions = farmerStore.getFarmers();

  if (farmersOptions.length == 0 || contextStore.getIsOffline == false) {
    farmersOptions = await api.getFarmers();
    farmersOptions.forEach((data) => {
      farmerStore.addNewFarmer(data);
    });
  }

  await hybridStore.initHybrids();
  hybridsOptions = hybridStore.getHybrids();

  if (hybridsOptions.length == 0 || contextStore.getIsOffline == false) {
    hybridsOptions = await api.getHybrids();
    hybridsOptions.forEach((data) => {
      hybridStore.addNewHybrid(data);
    });
  }

  farmersOptions = await utils.convertToSelectObject(farmersOptions);
  hybridsOptions = await utils.convertToSelectObject(hybridsOptions);
  farmerReady.value = true;
  hybridReady.value = true;
});

watch(
  () => farmerReady.value,
  (n, o) => {
    if (form.farmer_id && n) {
      const d = farmersOptions.filter((e) => e.value == form.farmer_id);
      farmerSelected.value = d[0] ?? {};
    }
  }
);

const submit = async () => {
  hybridSelected.value.forEach((h) => {
    form.hybrids.push(h.value);
  });
  form.farmer_id = farmerSelected?.value?.value;

  emits('submitMaster', form);
  return;
};
</script>
<template>
  <form class="space-y-4 text-left" action="#" @submit.prevent="submit">
    <div class="grid grid-cols-6 gap-6">
      <div class="col-span-6 sm:col-span-2">
        <Label value="Distance (Km)" />
        <input
          class="mt-1 block w-full rounded-lg resize-y"
          type="text"
          v-model="form.distance"
        />
      </div>

      <div class="col-span-6 sm:col-span-2">
        <Label for="date" value="Date" />
        <input
          id="date"
          type="date"
          :max="utils.getCurrentDate()"
          class="mt-1 block w-full rounded-lg"
          v-model="form.date"
          disabled
        />
      </div>
      <div class="col-span-6 sm:col-span-2">
        <Label for="activity_type" value="Activity Type" />
        <input
          id="activity_type"
          type="text"
          v-model="form.activity_type"
          class="mt-1 block w-full rounded-lg"
          disabled
        />
      </div>

      <div class="col-span-6 sm:col-span-6">
        <Label for="farmer_id" value="Farmer" class="required" />
        <VueSelect
          v-if="farmerReady"
          v-model="farmerSelected"
          :options="farmersOptions"
          label="label"
          value="value"
          placeholder="Select"
          :searchable="true"
          class="block w-full my-1 bg-white"
        >
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              :required="!farmerSelected"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </VueSelect>
      </div>
      <div class="col-span-6 sm:col-span-6">
        <Label for="hybrids" value="Hybrids" />
        <VueSelect
          v-if="hybridReady"
          v-model="hybridSelected"
          :options="hybridsOptions"
          label="label"
          value="value"
          placeholder="Select"
          :searchable="true"
          multiple
          class="block w-full my-1 bg-white"
        />
      </div>
      <div class="col-span-6 sm:col-span-6">
        <Label for="description" value="Description" class="required" />
        <textarea
          name=""
          id="description"
          cols="30"
          rows="5"
          class="mt-1 w-full rounded-lg"
          v-model="form.description"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="feedback" value="Feedback" />
        <input
          id="feedback"
          type="text"
          class="mt-1 block w-full rounded-lg"
          v-model="form.feedback"
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="planting_area" value="Plating Area" class="required" />
        <input
          id="planting_area"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.planting_area"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="5"
          min="0"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="attendance" value="Attendance" class="required" />
        <input
          id="attendance"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.attendance"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="5"
          min="0"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="budget_spent" value="Budget Spend" class="required" />
        <input
          id="budget_spent"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.budget_spent"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="7"
          min="0"
          required
        />
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="start_date" value="Upload Media" />
        <input
          id="files"
          type="file"
          class="mt-1 block w-full rounded-lg shadow-sm"
          @input="form.files = Array.from($event.target.files)"
          multiple
        />
        <p class="text-sm text-gray-500 mt-2">max size per file: 10mb</p>
        <!-- <template v-for="(f, index) in form.files" :key="f.file">
        <input-error
          :message="form.errors?.[`files.${index}`]"
          class="mt-2"
          v-if="form.errors?.[`files.${index}`]"
        />
      </template> -->
      </div>
      <div class="col-span-6 sm:col-span-3">
        <Label for="yields_Kg" value="Yields Kg" class="required" />
        <input
          id="yields_Kg"
          type="number"
          class="mt-1 block w-full rounded-lg"
          v-model="form.yields_Kg"
          oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
          @focus="utils.onFocusDefaultVal($event)"
          maxlength="5"
          min="0"
          required
        />
      </div>

      <div class="flex justify-end col-span-6">
        <button
          type="submit"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg font-semibold text-xs tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25 transition ml-2 bg-green-500 text-white"
        >
          Check Out
        </button>
      </div>
    </div>
  </form>
</template>
